import Page from "components/Page";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import Card from "components/Card";
import { TabContent, TabPane, Nav, NavItem, NavLink } from "reactstrap";
import {
  Suspense,
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import Loader from "components/Loader";
import { withTranslation } from "react-i18next";
import { Portal } from "react-overlays";
import StickySidebar from "accountOwner/pages/Scheduler/components/StickySidebar";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from "react-accessible-accordion";
import EmployeesFilter from "accountOwner/pages/Scheduler/components/SchedulerFilters/EmployeesFilter";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import CredentialGlobalTable, {
  sortingVariables,
} from "./CredentialGlobalTable";
import AddEditLincenseeModal from "../Auth/components/Profile/AddEditLincenseeModal";
import SearchBar from "accountOwner/pages/Notes/component/Filters/SearchBar";
import OfficeFilter from "accountOwner/pages/Scheduler/components/SchedulerFilters/OfficeFilter";
import CredentialTypeStatus from "./CredentialTypeStatus";
import ReactDatePickerCommon from "components/ReactDatePickerCommon";
import { useCredentialsFilter } from "./hooks/useCredentialsFilter";
import { useCredentialsData } from "./hooks/useCredentialsData";
import ToggleSwitch from "components/ToggleSwitch";
import styles from "./CredentialsAndLicensingGlobalListing.module.scss";
import QuestionIcon from "assets/images/question_mark.svg";
import CredentialAndLicensingHelpGuide from "./CredentialAndLicensingHelpGuide";
import NotAccessible from "accountOwner/pages/ManagerDashboard/components/NotAccessible";
import DeleteDocumentModal from "../Auth/components/Profile/DeleteDocumentModal";
import Empty from "components/Empty";
import constants from "../../constants";
import { encodeId } from "utils";
import { useOwnerSpecificStaffDesignation } from "repositories/scheduler-repository";
import { useGetAdminStaffOfficeList } from "repositories/office-repository";
import { isOtherOwnerCredential } from "features/credentials/utils/normalizeCredentialMaster";
export const FILTER_STORAGE_KEY_STAFF = "credentialsAndLicensingFiltersStaff";
export const FILTER_STORAGE_KEY_OFFICE = "credentialsAndLicensingFiltersOffice";
const ACTIVE_TAB_STORAGE_KEY = "credentialsAndLicensingActiveTab";
const ACCORDION_STORAGE_KEY = "credentialsAndLicensingAccordion";

const CredentialsAndLicensingGlobalListing = ({ t }) => {
  const history = useHistory();

  const handleView = (row) => {
    const route =
      constants.routes.credentialsAndLicensing.credentialsAndLicensingGlobalDetail.replace(
        ":id",
        encodeId(row.id),
      );
    history.push({
      pathname: route,
      state: { isOtherOwner: isOtherOwnerCredential(row) },
    });
  };
  const location = useLocation();
  useEffect(() => {
    const searchString = location.search;
    if (searchString) {
      // Use the native URLSearchParams interface to parse the string
      const queryParams = new URLSearchParams(searchString);
      // Access specific parameters
      const query = queryParams.get("credential");
      if (query) {
        queryParams.delete("credential");
        history.replace({
          search: queryParams.toString(),
        });
        setTimeout(() => {
          handleView({ id: +query });
        }, 100);
      }
    }
  }, []);
  const [activeTab, setActiveTab] = useState(() => {
    // Load from localStorage on initial render
    const searchString = window.location.search;
    if (searchString) {
      const queryParams = new URLSearchParams(searchString);
      const tab = queryParams.get("tab");
      if (tab) {
        localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tab || "1");
        return tab || "1";
      }
    }
    const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    return savedTab || "1";
  });
  const [isSidebarActive, setSidebarActive] = useState(true);
  const {
    selectedId,
    commonOwnerData,
    isStaff,
    commonOwnerData: selectedOwner,
    dataAccessValues,
    isOwner,
  } = useAccountOwnerCommon();
  const [isModalOpen, setIsModalOpen] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const editDataRef = useRef(null);
  const [isAboutSchedulerModal, setIsAboutSchedulerModal] = useState(false);
  const canAccessThisPage = isOwner || (isStaff && dataAccessValues.isAdmin);
  const cannotAccessThisPage = !canAccessThisPage;
  const { data: designationData } = useOwnerSpecificStaffDesignation(
    { selectedId },
    { enabled: !!selectedId },
  );
  const { data: officeListData } = useGetAdminStaffOfficeList(selectedId, {
    enabled: !!selectedId,
  });
  const officeList = useMemo(() => {
    if (officeListData?.data?.length > 0) {
      return [
        { value: "", label: "All Offices" },
        ...officeListData?.data.map((res) => ({
          value: res.id,
          label: res.name,
        })),
      ];
    }
    return [];
  }, [officeListData]);
  const designationList = useMemo(() => {
    const values = [{ value: "", label: "All Roles" }];
    designationData?.designations_list?.forEach((item) => {
      values.push({ value: item.id, label: item.name });
    });
    return values;
  }, [designationData?.designations_list]);
  // Accordion state with sessionStorage persistence
  const [expandedAccordionItems, setExpandedAccordionItems] = useState(() => {
    // Load from sessionStorage on initial render
    try {
      const saved = sessionStorage.getItem(ACCORDION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that saved items are arrays
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (error) {
      console.error("Error loading accordion state:", error);
    }
    // Default: expand first item (Issue Date)
    return ["a"];
  });

  const handleAccordionChange = (uuids) => {
    setExpandedAccordionItems(uuids);
    try {
      sessionStorage.setItem(ACCORDION_STORAGE_KEY, JSON.stringify(uuids));
    } catch (error) {
      console.error("Error saving accordion state:", error);
    }
  };

  // Use custom hook for filter management
  const filters = useCredentialsFilter(activeTab);
  // Use custom hook for data fetching
  const { staffCredentials, officeCredentials } = useCredentialsData(
    activeTab,
    selectedId,
    filters,
    canAccessThisPage,
  );
  // Calendar container to render date picker outside overflow container
  const CalendarContainer = useCallback(({ children }) => {
    if (typeof document !== "undefined") {
      return <Portal container={document.body}>{children}</Portal>;
    }
    return <>{children}</>;
  }, []);

  const handleSidebarToggle = () => {
    setSidebarActive(!isSidebarActive);
  };

  const goToPreviousScreen = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push(
        isOwner
          ? constants.routes.accountOwner.offices
          : constants.routes.staff.offices,
      );
    }
  };
  const timerRef = useRef(null);
  const handleSearchTerm = (event) => {
    filters.setSearchValue(event.target.value);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      filters.setSearchValueApplied(event.target.value);
    }, 500);
  };
  const currentActiveTab = useMemo(() => {
    return { firstTab: activeTab === "1", secondTab: activeTab === "2" };
  }, [activeTab]);
  const handleEditCredential = (rowData) => {
    if (rowData?.isOtherOwner) return;
    // Map the row data to match the modal's expected structure
    editDataRef.current = {
      ...rowData,
      type: rowData.credentialType, // Map credentialType to type for the modal
    };
    setIsModalOpen(
      currentActiveTab.firstTab
        ? "edit_staff_credential"
        : "edit_office_credential",
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(null);
    editDataRef.current = null;
  };

  // Save activeTab to localStorage whenever it changes
  const setActiveTabToLocalStorage = useCallback((activeTab) => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
    setActiveTab(activeTab);
  }, []);
  const handleDeleteCredential = (rowData) => {
    if (rowData?.isOtherOwner) return;
    editDataRef.current = {
      ...rowData,
      type: rowData.credentialType, // Map credentialType to type for the modal
    };
    setIsDeleteModalOpen(true);
  };

  return (
    <Page
      onBack={goToPreviousScreen}
      className={"scheduler-page " + styles["credential-wrapper-box"]}
    >
      <div className="d-flex justify-content-between page_title_margin_top marginBottom30 align-items-center">
        <div className="d-flex flex-column align-items-start">
          <h2 className="page-title mb-0 margin-fix">
            {t("accountOwner.credentialsAndLicensing")}
          </h2>
          {isStaff && selectedOwner?.id && (
            <div className="show-owner-name-text">
              {t("accountOwner.ownerAdminHeadingForCredentialsAndLicensing")}
              <span>
                {` '${selectedOwner?.firstName} ${selectedOwner?.lastName}' `}
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          className={"question_btn"}
          onClick={() => setIsAboutSchedulerModal(true)}
        >
          <img src={QuestionIcon} alt="QuestimIcon" />
          <p className="m-0">{t("navbar.help")}</p>
        </button>
      </div>
      {(staffCredentials.isLoading || officeCredentials.isLoading) && (
        <Loader />
      )}
      <Card cursor={"default"}>
        <div className="common-tabs scheduler-tabs">
          <Nav tabs>
            <NavItem>
              <NavLink
                className={currentActiveTab.firstTab ? "active" : ""}
                onClick={() => {
                  setActiveTabToLocalStorage("1");
                  filters.handleSetSortByAndIsDescending(
                    sortingVariables.UserName,
                    true,
                  );
                }}
              >
                {t("accountOwner.staffLicenses")}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={currentActiveTab.secondTab ? "active" : ""}
                onClick={() => {
                  setActiveTabToLocalStorage("2");
                  filters.handleSetSortByAndIsDescending(
                    sortingVariables.UserName,
                    true,
                  );
                }}
              >
                {t("accountOwner.officeLicensesPermits")}
              </NavLink>
            </NavItem>
          </Nav>
          <div className={"scheduler-tabs-main-wrapper"}>
            <StickySidebar
              isSidebarActive={isSidebarActive}
              handleSidebarToggle={handleSidebarToggle}
              resetFilter={() => {}}
            >
              <div className={`${styles["filter-title-box"]}`}>
                <span>
                  {t("notes.filters")}
                  {!!filters.isFilterApplied && (
                    <span className={styles.red_dot}></span>
                  )}
                </span>
              </div>
              <div className={`${styles["filter-toggle-box"]}`}>
                <ToggleSwitch
                  className="mt-0"
                  label="bill-me-later-toggle"
                  value={filters.radioChecked}
                  onChange={(e) => filters.setRadioChecked(e.target.checked)}
                />
                <span> {t("accountOwner.showExpiringSoon")}</span>
              </div>
              <Accordion
                preExpanded={expandedAccordionItems}
                className="filter-accordion"
                allowZeroExpanded
                allowMultipleExpanded
                onChange={handleAccordionChange}
              >
                <AccordionItem uuid="a">
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      {t("accountOwner.issueDate")}
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <div className="d-flex gap-base">
                      <div className="c-field w-100">
                        <label>{t("accountOwner.from")}</label>
                        <div className="d-flex inputdate">
                          <ReactDatePickerCommon
                            dateFormat="dd-MM-yyyy"
                            className="c-form-control"
                            onChange={(e) => {
                              filters.setStartDate(e);
                            }}
                            selected={filters.startDate}
                            popperContainer={CalendarContainer}
                          />
                        </div>
                      </div>
                      <div className="c-field w-100">
                        <label>{t("accountOwner.to")}</label>
                        <div className="d-flex inputdate">
                          <ReactDatePickerCommon
                            dateFormat="dd-MM-yyyy"
                            className="c-form-control"
                            onChange={(e) => {
                              filters.setEndDate(e);
                            }}
                            minDate={filters.startDate}
                            selected={filters.endDate}
                            popperContainer={CalendarContainer}
                          />
                        </div>
                      </div>
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
                {activeTab === "1" ? (
                  <AccordionItem
                    uuid="b"
                    className="accordion__item border-0 m-0 p-0"
                  >
                    <AccordionItemHeading>
                      <AccordionItemButton>
                        {t("accountOwner.staff")}
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>
                      <EmployeesFilter
                        selectedEmpFilter={filters.selectedEmpFilter}
                        setEmpFilter={filters.setSelectedEmpFilter}
                        ownerId={selectedId}
                        ownerData={commonOwnerData || {}}
                        selectedOfficeFilter={officeList.flatMap(
                          (item) => item.value || [],
                        )}
                        selectedRoles={filters.selectedRoles}
                        setSelectedRoles={filters.setSelectedRoles}
                        resetSearchTrigger={filters.resetSearchTrigger}
                        enabledOfficeFilter
                        hideOwner={isOwner}
                        idSelectionType="userId"
                        deduplicateByUserId={true}
                        showDesignationName={false}
                        extraRoles={designationList}
                        extraOffices={officeList}
                      />
                    </AccordionItemPanel>
                  </AccordionItem>
                ) : (
                  <AccordionItem
                    uuid="b"
                    className="accordion__item border-0 m-0 p-0"
                  >
                    <AccordionItemHeading>
                      <AccordionItemButton>
                        {t("accountOwner.office")}
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>
                      <OfficeFilter
                        setOfficeFilter={filters.setSelectedOfficeFilter}
                        selectedOffice={filters.selectedOfficeFilter}
                        selectedOwnerId={selectedId}
                        includeOwnerOffices={true}
                        allElement={() => null}
                        extraOffices={officeListData?.data || []}
                      />
                    </AccordionItemPanel>
                  </AccordionItem>
                )}
                <AccordionItem uuid="e">
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      {t("accountOwner.documentationStatus")}
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <CredentialTypeStatus
                      documentationEnabled
                      selectedValues={filters.documentationStatus}
                      setSelectedValues={filters.setDocumentationStatus}
                    />
                  </AccordionItemPanel>
                </AccordionItem>
                <AccordionItem uuid="c">
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      {t("accountOwner.credentialStatus")}
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <CredentialTypeStatus
                      statusEnabled={true}
                      selectedValues={filters.statuses}
                      setSelectedValues={filters.setStatuses}
                    />
                  </AccordionItemPanel>
                </AccordionItem>
                <AccordionItem uuid="d">
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      {t("accountOwner.credentialType")}
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <CredentialTypeStatus
                      selectedValues={filters.credentialTypes}
                      setSelectedValues={filters.setCredentialTypes}
                    />
                  </AccordionItemPanel>
                </AccordionItem>
              </Accordion>
              <div className="filter-btn-box filter-btn-box--sticky-footer ">
                <button
                  className={"button button-round button-shadow mr-3"}
                  title={t("apply")}
                  onClick={filters.handleApplyFilter}
                >
                  {" "}
                  {t("apply")}
                </button>
                <button
                  className={
                    "button button-round button-border button-dark reset-btn"
                  }
                  onClick={filters.resetFilter}
                  title={t("reset")}
                >
                  {t("reset")}
                </button>
                <button
                  className={
                    "button button-round button-border button-dark cancel-btn bg-transparent"
                  }
                  title={t("close")}
                  onClick={handleSidebarToggle}
                >
                  {t("close")}
                </button>
              </div>
            </StickySidebar>
            <TabContent
              activeTab={activeTab}
              className={
                cannotAccessThisPage ||
                (currentActiveTab.firstTab &&
                  !staffCredentials.isLoading &&
                  (!staffCredentials?.data ||
                    staffCredentials?.data?.length === 0)) ||
                (currentActiveTab.secondTab &&
                  !officeCredentials.isLoading &&
                  (!officeCredentials?.data ||
                    officeCredentials?.data?.length === 0))
                  ? "w-100"
                  : styles["tab-content-box"]
              }
            >
              {canAccessThisPage && (
                <div
                  className={` ${styles["listing-search-box"]} ${isSidebarActive ? styles["listing-search-box-mobile"] : ""}`}
                >
                  <SearchBar
                    parentClass={` ${styles["listing-search-bar"]} `}
                    searchValue={filters.searchValue}
                    handleSearchTerm={handleSearchTerm}
                    t={t}
                    placeholder={t("accountOwner.search")}
                  />

                  <button
                    onClick={() => {
                      setIsModalOpen(
                        currentActiveTab.firstTab
                          ? "add_staff_credential"
                          : "add_office_credential",
                      );
                    }}
                    className="button button-round  button-shadow"
                    title={t("accountOwner.addStaff")}
                  >
                    {currentActiveTab.firstTab
                      ? t("accountOwner.addStaffCredential")
                      : t("accountOwner.addOfficeCredential")}
                  </button>
                </div>
              )}

              <Suspense fallback={<Loader />}>
                {cannotAccessThisPage ? (
                  <div
                    className="w-100 not-accessible center_property"
                    style={{ height: "300px" }}
                  >
                    <NotAccessible Message={t("dashBoard.notAccessible")} />
                  </div>
                ) : (
                  <>
                    <TabPane tabId="1">
                      {!staffCredentials.isLoading &&
                      (!staffCredentials?.data ||
                        staffCredentials?.data?.length === 0) ? (
                        <div
                          className="w-100 center_property"
                          style={{ height: "300px" }}
                        >
                          <Empty
                            Message={t(
                              "accountOwner.noStaffCredentialsAvailable",
                            )}
                          />
                        </div>
                      ) : (
                        <CredentialGlobalTable
                          data={staffCredentials?.data || []}
                          isLoading={staffCredentials.isLoading}
                          refetchData={staffCredentials.refetch}
                          sortBy={filters.sortBy}
                          handleSetSortByAndIsDescending={
                            filters.handleSetSortByAndIsDescending
                          }
                          isDescending={filters.isDescending}
                          onEdit={handleEditCredential}
                          onDelete={handleDeleteCredential}
                          pageNumber={filters.pageNumber}
                          setPageNumber={filters.setPageNumber}
                          totalItems={staffCredentials.totalItems}
                        />
                      )}
                    </TabPane>
                    <TabPane tabId="2">
                      {!officeCredentials.isLoading &&
                      (!officeCredentials?.data ||
                        officeCredentials?.data?.length === 0) ? (
                        <div
                          className="w-100 center_property"
                          style={{ height: "300px" }}
                        >
                          <Empty
                            Message={t(
                              "accountOwner.noOfficeCredentialsAvailable",
                            )}
                          />
                        </div>
                      ) : (
                        <CredentialGlobalTable
                          data={officeCredentials?.data || []}
                          isLoading={officeCredentials.isLoading}
                          refetchData={officeCredentials.refetch}
                          handleSetSortByAndIsDescending={
                            filters.handleSetSortByAndIsDescending
                          }
                          sortBy={filters.sortBy}
                          isDescending={filters.isDescending}
                          officeTable
                          onEdit={handleEditCredential}
                          onDelete={handleDeleteCredential}
                          pageNumber={filters.pageNumber}
                          setPageNumber={filters.setPageNumber}
                          totalItems={officeCredentials.totalItems}
                        />
                      )}
                    </TabPane>
                  </>
                )}
              </Suspense>
            </TabContent>
            {isModalOpen && (
              <AddEditLincenseeModal
                isModalOpen={isModalOpen !== null}
                closeModal={handleCloseModal}
                heading={isModalOpen.replaceAll("_", " ")}
                addStaffOffice={isModalOpen}
                editData={editDataRef.current}
                refetchData={() => {
                  if (currentActiveTab.firstTab) {
                    staffCredentials.refetch();
                  } else {
                    officeCredentials.refetch();
                  }
                }}
              />
            )}
          </div>
        </div>
      </Card>
      {isDeleteModalOpen && (
        <DeleteDocumentModal
          credentialId={editDataRef.current?.id}
          onClose={() => {
            setIsDeleteModalOpen(false);
            editDataRef.current.current = null;
          }}
          refetchData={() => {
            if (currentActiveTab.firstTab) {
              staffCredentials.refetch();
            } else {
              officeCredentials.refetch();
            }
          }}
        />
      )}
      {isAboutSchedulerModal && (
        <CredentialAndLicensingHelpGuide
          isOpen={isAboutSchedulerModal}
          onClose={() => setIsAboutSchedulerModal(false)}
          isOfficeHelpModal={currentActiveTab.secondTab}
        />
      )}{" "}
    </Page>
  );
};
export default withTranslation()(CredentialsAndLicensingGlobalListing);
